import type {UseQueryResult} from '@tanstack/react-query'
import {useMemo} from 'react'

import {NO_GROUP_VALUE} from '../../../api/memex-items/contracts'
import {useFilteredItems} from '../../../features/filtering/hooks/use-filtered-items'
import {getGroupByFieldId} from '../../../features/grouping/get-group-by-field'
import {getGroupingMetadataFromServerGroupValue} from '../../../features/grouping/get-grouping-metadata'
import {useHorizontalGroupedBy} from '../../../features/grouping/hooks/use-horizontal-grouped-by'
import {useVerticalGroups} from '../../../features/grouping/hooks/use-vertical-groups'
import {isVerticalGroupColumn} from '../../../models/column-capabilities'
import type {ColumnModel} from '../../../models/column-model'
import type {
  GroupedHorizontalGroup,
  HorizontalGroup,
  HorizontalGrouping,
  ItemDataForSwimlaneCell,
  ItemDataForVerticalColumn,
  ItemsGroupedByVerticalColumn,
} from '../../../models/horizontal-group'
import {MissingVerticalGroupId, type VerticalGroup} from '../../../models/vertical-group'
import {useBulkAddNotifications} from '../../../state-providers/bulk-add-notifications/bulk-add-notifications-state-provider'
import {useAllColumns} from '../../../state-providers/columns/use-all-columns'
import {usePaginatedItemsQueryStatus} from '../../../state-providers/data-refresh/use-paginated-items-query-status'
import {createGroupedItemsId} from '../../../state-providers/memex-items/queries/query-keys'
import type {
  MemexGroupsPageQueryData,
  PaginatedGroupQueryData,
} from '../../../state-providers/memex-items/queries/types'
import {
  type GroupedItemQueries,
  type GroupsById,
  usePaginatedMemexItemsQuery,
} from '../../../state-providers/memex-items/queries/use-paginated-memex-items-query'
import {Resources} from '../../../strings'

const defaultGroupByFieldOptions: Array<VerticalGroup> = []
/**
 * Filters and groups items depending on the current view.
 * Steps:
 * 1. Apply the current filter to the full list of items.
 * 2. If the current view is not grouped, we will fall back to the `Status` field.
 * 3. If the current view is grouped, we will create a list of groups, each with a list of items, based on the vertical grouping field.
 * @returns An array of items grouped by the vertical group they belong to
 */
export function useBoardItems() {
  const {filteredItems} = useFilteredItems()
  const {groupedByColumnId, groupByFieldOptions = defaultGroupByFieldOptions} = useVerticalGroups()
  const {groupedByColumn: horizontalGroupedByColumn, collapsedGroups} = useHorizontalGroupedBy()
  const {allColumns} = useAllColumns()
  const {shouldShowSkeleton} = useBulkAddNotifications()
  const compatibleColumns = useMemo(() => allColumns.filter(c => isVerticalGroupColumn(c.dataType)), [allColumns])

  const groupByFieldId = useMemo(
    () => getGroupByFieldId(compatibleColumns, groupedByColumnId),
    [compatibleColumns, groupedByColumnId],
  )

  const groupByField = useMemo(() => allColumns.find(c => c.id === groupByFieldId), [allColumns, groupByFieldId])

  const {queriesForGroups, queriesForSecondaryGroups, groupedItemQueries, groupsById} = usePaginatedMemexItemsQuery()
  const groupedItems: HorizontalGrouping = useMemo(() => {
    if (horizontalGroupedByColumn) {
      // we want to render swimlanes for a PWL project
      const {horizontalGroups, allItemsByVerticalGroup} = formatGroupsFromServer(
        queriesForGroups,
        queriesForSecondaryGroups,
        groupedItemQueries,
        groupsById,
        horizontalGroupedByColumn,
      )

      collapseHorizontalGroups(horizontalGroups, collapsedGroups)

      return {
        allItemsByVerticalGroup,
        horizontalGroups,
        isHorizontalGrouped: true,
        horizontalGroupedByColumn,
      }
    }

    const allItemsByVerticalGroup = formatGroupsFromServer(
      queriesForGroups,
      queriesForSecondaryGroups,
      groupedItemQueries,
      groupsById,
      undefined,
    ).allItemsByVerticalGroup

    return {
      isHorizontalGrouped: false as const,
      allItemsByVerticalGroup,
      horizontalGroups: [
        {
          value: Resources.undefined,
          isCollapsed: false,
          itemsByVerticalGroup: allItemsByVerticalGroup,
          rows: filteredItems,
          totalCount: filteredItems.length,
        },
      ],
    }
  }, [
    horizontalGroupedByColumn,
    queriesForGroups,
    queriesForSecondaryGroups,
    groupedItemQueries,
    groupsById,
    filteredItems,
    collapsedGroups,
  ])

  const {isFetching, hasPlaceholderItems} = usePaginatedItemsQueryStatus()
  const isLoading = queriesForGroups.every(query => query.isLoading) || shouldShowSkeleton
  const showItemsSkeleton = (isFetching && !hasPlaceholderItems) || shouldShowSkeleton

  return useMemo(() => {
    return {
      groupByFieldOptions,
      groupByFieldId,
      groupByField,
      compatibleColumns,
      filteredItems,
      groupedItems,
      isLoading,
      showItemsSkeleton,
    }
  }, [
    groupByFieldOptions,
    groupByFieldId,
    groupByField,
    compatibleColumns,
    filteredItems,
    groupedItems,
    isLoading,
    showItemsSkeleton,
  ])
}

/**
 * Utility function that prepares items grouped server-side for use by the Board component.
 * If a horizontal column model is provided, horizontal groups (i.e. swimlanes) will be created as well.
 * @param queriesForGroups an array containing query data for groups
 * @param queriesForSecondaryGroups an array containing query data for secondary groups
 * @param groupedItemQueries queries of items grouped by their group id
 * @param groupsById allows for looking up a group and its total count by a group id
 * @param horizontalColumnModel the column to use for the secondary groups. Needed so that we can look up
 * group metadata for the secondary groups. If undefined, we should not attempt to group
 * the items by the secondary groups.
 * @returns an object with two pieces of data.
 * 1. allItemsByVerticalGroup: All of the items on the client grouped by the primary group.
 * 2. horizontalGroups: All of the information needed to render each swimlane, including all of the items
 * in the swimlane, items in the swimlane grouped by each vertical group and group metadata for the swimlane.
 */
export function formatGroupsFromServer(
  queriesForGroups: Array<UseQueryResult<MemexGroupsPageQueryData>>,
  queriesForSecondaryGroups: Array<UseQueryResult<MemexGroupsPageQueryData>>,
  groupedItemQueries: GroupedItemQueries,
  groupsById: GroupsById,
  horizontalColumnModel: ColumnModel | undefined,
): {allItemsByVerticalGroup: ItemsGroupedByVerticalColumn; horizontalGroups: Array<GroupedHorizontalGroup>} {
  const allItemsByVerticalGroup: {[x: string]: ItemDataForVerticalColumn} = {}
  const horizontalGroups: Array<GroupedHorizontalGroup> = []

  const allPrimaryGroups = queriesForGroups.flatMap(query => query?.data?.groups || [])

  for (const primaryGroup of allPrimaryGroups) {
    const itemQueries = groupedItemQueries[primaryGroup.groupId]
    const items = itemQueries?.flatMap(q => q.data?.nodes || []) || []

    const groupKey = getGroupKey(primaryGroup)

    const group = groupsById[primaryGroup.groupId]
    const totalCount = group?.totalCount.value

    allItemsByVerticalGroup[groupKey] = {items, groupId: primaryGroup.groupId, totalCount}
  }

  if (!horizontalColumnModel) {
    return {allItemsByVerticalGroup, horizontalGroups}
  }

  const allSecondaryGroups = queriesForSecondaryGroups.flatMap(query => query?.data?.groups || [])

  for (const secondaryGroup of allSecondaryGroups) {
    const sourceObject = getGroupingMetadataFromServerGroupValue(
      horizontalColumnModel,
      secondaryGroup.groupValue,
      secondaryGroup.groupMetadata,
    )
    const itemsByVerticalGroup: {[x: string]: ItemDataForSwimlaneCell} = {}
    for (const primaryGroup of allPrimaryGroups) {
      const groupedItemsId = createGroupedItemsId({
        groupId: primaryGroup.groupId,
        secondaryGroupId: secondaryGroup.groupId,
      })
      const itemQueries = groupedItemQueries[groupedItemsId]
      const items = itemQueries?.flatMap(q => q.data?.nodes || []) || []

      const groupKey = getGroupKey(primaryGroup)
      const hasMoreItems = itemQueries ? !!itemQueries[itemQueries.length - 1]?.data?.pageInfo.hasNextPage : false

      itemsByVerticalGroup[groupKey] = {items, groupId: primaryGroup.groupId, hasMoreItems}
    }
    const group = groupsById[secondaryGroup.groupId]
    const totalCount = group?.totalCount.value || 0

    const rows = groupedItemQueries[secondaryGroup.groupId]?.flatMap(q => q.data?.nodes || []) || []
    if (sourceObject) {
      horizontalGroups.push({
        // We use a default value of `false` here, and will set the actual
        // value of the property in the `collapseHorizontalGroups` helper
        isCollapsed: false,
        itemsByVerticalGroup,
        rows,
        sourceObject,
        value: secondaryGroup.groupValue,
        serverGroupId: secondaryGroup.groupId,
        totalCount,
      })
    }
  }

  return {allItemsByVerticalGroup, horizontalGroups}
}

/**
 * Helper for determining a key for a group.
 * @param group The group we want to try to resolve a key for
 * @returns a string representation of the group's key
 */
function getGroupKey(group: PaginatedGroupQueryData): string {
  // Use MissingVerticalGroupId for the 'no value' group
  // i.e., a group without an associated metadata object.
  // TODO: Remove dependence on MissingVerticalGroupId
  // https://github.com/github/projects-platform/issues/2127
  return group.groupValue === NO_GROUP_VALUE ? MissingVerticalGroupId : group.groupId
}

/**
 * Iterate over the horizontal groups and check the value of each group against the list of collapsed groups.
 * If the value is present in that list, update the `isCollapsed` property of the horizontal group to `true`.
 */
function collapseHorizontalGroups(horizontalGroups: Array<HorizontalGroup>, collapsedGroups: Array<string>) {
  for (const horizontalGroup of horizontalGroups) {
    if (collapsedGroups.includes(horizontalGroup.value)) {
      horizontalGroup.isCollapsed = true
    }
  }
}
